import { useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { StoryCardStatus } from '../../services';
import { getStoryFeed, getStoryFeedLoadMore } from '../../slices';
import { STORY_TAB_ITEMS_ADMIN_COOKIE_KEY } from '../constants';
import { StoryTabType } from '../models';
import { getStorySectionByTabType, isAdminLayout } from '../utils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useLastDate } from './useLastDate';

interface UseStoryFeedArgs {
  storyId: number;
  storyTabType?: StoryTabType;
}

export const useStoryFeed = ({ storyId, storyTabType }: UseStoryFeedArgs) => {
  const dispatch = useAppDispatch();

  const { getDateRange } = useLastDate();

  const [cookies] = useCookies([STORY_TAB_ITEMS_ADMIN_COOKIE_KEY]);

  const { page } = useAppSelector(({ storyFeed }) => storyFeed);

  const { requestFilter = {} } = { ...cookies[STORY_TAB_ITEMS_ADMIN_COOKIE_KEY] };

  const { dateRange, ...requestFilterRest } = requestFilter;

  const filter = useMemo(() => {
    if (!isAdminLayout()) {
      return { status: [StoryCardStatus.PUBLISHED] };
    }

    const filter = { ...requestFilterRest, ...getDateRange(dateRange) };

    if (!storyTabType) {
      return filter;
    }

    return { ...filter, section: getStorySectionByTabType(storyTabType) };
  }, [dateRange, getDateRange, requestFilterRest, storyTabType]);

  const getStoryFeedItems = useCallback(
    () => dispatch(getStoryFeed({ storyId, filter })),
    [dispatch, filter, storyId]
  );

  const storyFeedItemsLoadMore = useCallback(
    () => dispatch(getStoryFeedLoadMore({ storyId, page: page + 1, filter })),
    [dispatch, filter, page, storyId]
  );

  return { getStoryFeedItems, storyFeedItemsLoadMore };
};
